import AddCircleOutline from '@material-ui/icons/AddCircleOutline';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { ButtonsGroup } from '@monorepo/shared/componentsV2/ButtonsGroup';
import { Description } from '@monorepo/shared/componentsV2/Description';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { Main } from '@monorepo/shared/componentsV2/Main';
import { TableBulkActionsModeProvider } from '@monorepo/shared/componentsV2/Table/TableBulkActionsModeContext';
import { useDisableSubnav } from '@monorepo/shared/hooks/subnav/useDisableSubnav';
import { useOpenClose } from '@monorepo/shared/hooks/useOpenClose';
import {
  contentPaddingValue,
  fillHeightAndScrollable,
  page,
  verticalLayout,
} from '@monorepo/shared/styles/layout';
import { header4 } from '@monorepo/shared/styles/text';
import { BULK_RESOURCE_DELETE_LIMIT } from 'mapistry-shared';
import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { EditResourceModal } from '../../EditResourceModal';
import { ResourcesUploadModal } from '../../ResourcesUploadModal';
import { ViewResourcesTable } from '../../ViewResourcesTable/ViewResourcesTable';
import { EditResourceTypeHeader, WizardStep } from '../EditResourceTypeHeader';

const Page = styled.div`
  ${verticalLayout}
  ${page}
`;

const StyledMain = styled(Main)`
  ${fillHeightAndScrollable}
`;

const Content = styled.div`
  ${verticalLayout}
`;

const HeaderContainer = styled.div`
  margin: ${contentPaddingValue} ${contentPaddingValue} 0.5rem
    ${contentPaddingValue};
`;

const Subheader = styled.h2`
  ${header4}
`;

const AddResourceButtonContainer = styled(ButtonsGroup)`
  margin-right: ${contentPaddingValue};
`;

const TableContainer = styled.div`
  ${fillHeightAndScrollable}
`;

function AddResourcesPageContent() {
  useDisableSubnav();
  const { organizationId, resourceTypeId } = useParams();

  const [isEditModalOpen, openEditModal, closeEditModal] = useOpenClose();
  const [isBulkUploadModalOpen, openBulkUploadModal, closeBulkUploadModal] =
    useOpenClose();

  const [resourceIdForEdit, setResourceIdForEdit] = useState<
    string | undefined
  >(undefined);

  const openResourceModal = useCallback(
    (resourceId?: string) => {
      setResourceIdForEdit(resourceId);
      openEditModal();
    },
    [setResourceIdForEdit, openEditModal],
  );

  const handleAddNew = useCallback(() => {
    openResourceModal();
  }, [openResourceModal]);

  const [pageNumber, setPageNumber] = useState(0);

  return (
    <Page>
      <EditResourceTypeHeader wizardStep={WizardStep.RESOURCES} />

      <StyledMain>
        <Content>
          <HeaderContainer>
            <Subheader>Add Resources</Subheader>
            <Description>
              Add or upload Resources along with any associated fields.
            </Description>
          </HeaderContainer>

          <TableContainer>
            <ViewResourcesTable
              onEditResource={(resourceId: string) =>
                openResourceModal(resourceId)
              }
              onPageChange={setPageNumber}
              pageNumber={pageNumber}
              addResourceButtons={
                <AddResourceButtonContainer>
                  <Button color="primary" onClick={handleAddNew}>
                    <AddCircleOutline />
                    Add Resource
                  </Button>
                  <Button color="primary" onClick={openBulkUploadModal}>
                    <ArrowUpward />
                    Upload from file
                  </Button>
                </AddResourceButtonContainer>
              }
            />
          </TableContainer>
        </Content>
      </StyledMain>
      {isEditModalOpen && (
        <EditResourceModal
          organizationId={organizationId}
          resourceTypeId={resourceTypeId}
          open={isEditModalOpen}
          resourceId={resourceIdForEdit}
          onClose={closeEditModal}
        />
      )}
      {isBulkUploadModalOpen && organizationId && resourceTypeId && (
        <ErrorBoundary onError={closeBulkUploadModal}>
          <ResourcesUploadModal
            isOpen={isBulkUploadModalOpen}
            resourceTypeId={resourceTypeId}
            onClose={closeBulkUploadModal}
            organizationId={organizationId}
          />
        </ErrorBoundary>
      )}
    </Page>
  );
}

export const AddResourcesPage = () => (
  <ErrorBoundary>
    <TableBulkActionsModeProvider
      bulkRowDeleteLimit={BULK_RESOURCE_DELETE_LIMIT}
    >
      <AddResourcesPageContent />
    </TableBulkActionsModeProvider>
  </ErrorBoundary>
);
